import React, {useState, useMemo, useCallback} from "react";
import {
    Search, Spinner, IconButton, Button, ButtonIcon, Text, Stack,
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
    Tooltip, TooltipTrigger, TooltipContent
} from "@nike/podium";
import {Check, Close, Tip, AlertCircle} from "@nike/nike-design-system-icons";
import {ImbalanceItem, BucketConfig} from "../../common/data/types";
import {adjUnits} from "../../common/data/imbalanceHelpers";

type BucketProps = {
    config: BucketConfig;
    rows: ImbalanceItem[];
    filter: string;
    onFilterChange: (val: string) => void;
    onRemoveRecord: (origIdx: number) => void;
    onPerformAction: () => void;
    isDone: boolean;
    startOpen: boolean;
};

type ActionState = 'idle' | 'loading' | 'success';

export function Bucket({
    config, rows, filter, onFilterChange, onRemoveRecord,
    onPerformAction, isDone, startOpen
}: BucketProps) {
    const [isOpen, setIsOpen] = useState(startOpen);
    const [actionState, setActionState] = useState<ActionState>('idle');
    const [statusText, setStatusText] = useState('');

    const totalUnits = useMemo(() => rows.reduce((s, item) => s + adjUnits(item), 0), [rows]);

    const visible = useMemo(() => {
        const q = filter.toLowerCase();
        return rows.map((item, i) => ({item, i})).filter(({item}) => !q || item.material.toLowerCase().includes(q));
    }, [rows, filter]);

    const handlePerformAction = useCallback(() => {
        if (actionState !== 'idle' || rows.length === 0) return;
        setActionState('loading');
        onPerformAction();

        setTimeout(() => {
            setActionState('success');
            const adjType = config.key === 'over' ? 'POSADJ' : 'NEGADJ';
            const ref = `${adjType}-${Date.now().toString(36).toUpperCase().slice(-6)}`;
            setStatusText(`${rows.length} lines · ${totalUnits} units · ${adjType} · ${ref}`);
        }, 1400);
    }, [actionState, rows, onPerformAction, config.key, totalUnits]);

    const renderActionButton = () => {
        if (actionState === 'loading') {
            return (
                <Button size="s" variant="outlined" disabled className="im-action-loading">
                    <ButtonIcon>
                        <Spinner size="small" />
                    </ButtonIcon>
                    Processing…
                </Button>
            );
        }
        if (actionState === 'success' || isDone) {
            return (
                <Button size="s" variant="outlined" disabled className="im-action-success">
                    <ButtonIcon>
                        <Check size="s" />
                    </ButtonIcon>
                    Done
                </Button>
            );
        }
        return (
            <Button
                size="s"
                disabled={rows.length === 0}
                onClick={handlePerformAction}
            >
                <ButtonIcon>
                    <Tip size="s" />
                </ButtonIcon>
                {config.btnLabel}
            </Button>
        );
    };

    const accordionValue = isOpen ? [config.key] : [];

    return (
        <Accordion
            type="multiple"
            value={accordionValue}
            onValueChange={(vals) => {
                if (!isDone) setIsOpen(vals.includes(config.key));
            }}
            className={`im-bucket ${isDone ? 'is-done' : ''}`}
        >
            <AccordionItem value={config.key}>
                <AccordionTrigger>
                    <div className="im-bucket-trigger-content">
                        <span className="im-bucket-name">{config.name}</span>
                        <div className="im-bucket-meta">
                            <strong>{rows.length}</strong>
                            <span>{isDone ? 'records adjusted' : 'records'}</span>
                            <span className="sep">&middot;</span>
                            <strong>{totalUnits}</strong>
                            <span style={isDone ? {color: 'var(--pds-color-text-success)'} : undefined}>
                                {isDone ? 'units adjusted' : 'units'}
                            </span>
                            <span className="sep">&middot;</span>
                            <span className={`im-badge im-${config.adjBadgeClass}`}>{config.adjBadgeText}</span>
                            <span className={`im-badge im-${config.lcBadgeClass}`}>
                                {config.lcDotColor && (
                                    <span className="im-lc-dot" style={{background: config.lcDotColor}} />
                                )}
                                {config.lcBadgeText}
                            </span>
                            <span className="im-bucket-action-lbl">{config.actionLabel}</span>
                            <span className="im-bkt-done-check">
                                <Check size="s" />
                                Done
                            </span>
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent>
                    <Stack horizontal align="center" gap="s" className="im-bucket-filter-bar">
                        <Search
                            id={`bucket-search-${config.key}`}
                            label={`Filter ${config.name} by material code`}
                            placeholder="Filter by material code…"
                            value={filter}
                            onValueChange={onFilterChange}
                        />
                        {filter && (
                            <Text font="body-4" as="span" color="secondary" className="im-bkt-match-count">
                                <strong>{visible.length}</strong> of {rows.length} shown
                            </Text>
                        )}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="im-limit-notice">
                                    <AlertCircle size="s" />
                                    Top 1,000 by persistent imbalance size
                                </span>
                            </TooltipTrigger>
                            <TooltipContent size="s">
                                For performance reasons, only the top 1,000 persistent imbalances per bucket are loaded
                            </TooltipContent>
                        </Tooltip>
                    </Stack>

                    {rows.length === 0 ? (
                        <Stack align="center" padding={20}>
                            <Text font="body-4" color="secondary">No records in this bucket.</Text>
                        </Stack>
                    ) : visible.length === 0 ? (
                        <Stack align="center" padding={20}>
                            <Text font="body-4" color="secondary">
                                No materials match "<strong>{filter}</strong>".
                            </Text>
                        </Stack>
                    ) : (
                        <div className="im-bc-table-wrap">
                            <table className="im-bc-table">
                                <thead>
                                    <tr>
                                        <th style={{width: 28}} />
                                        <th>Material</th>
                                        <th>Size</th>
                                        <th>Quality</th>
                                        <th>COO</th>
                                        <th>WMS Qty</th>
                                        <th>CI Bonded Qty</th>
                                        <th>CI Non-bonded Qty</th>
                                        <th>CI Total Qty</th>
                                        <th>Latest imbalance</th>
                                        <th>Persistent imbalance units to adjust</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map(({item, i}) => (
                                        <BucketRow
                                            key={`${item.material}-${i}`}
                                            item={item}
                                            onRemove={() => onRemoveRecord(i)}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <Stack horizontal align="center" gap="s" className="im-bucket-foot">
                        {renderActionButton()}
                        {statusText && (
                            <Text font="body-4" as="span" className={`im-status-chip ${actionState === 'success' ? 'done' : ''}`}>
                                {statusText}
                            </Text>
                        )}
                    </Stack>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}

type BucketRowProps = {
    item: ImbalanceItem;
    onRemove: () => void;
};

function BucketRow({item, onRemove}: BucketRowProps) {
    const {material, size, quality, coo, wmsQty, ciBondedQty, ciNonBondedQty, ciTotalQty, imbalance, persistentImbalance} = item;

    const renderImbalance = () => {
        if (wmsQty === 0) return <span className="im-iv im-iv-zero">{imbalance}</span>;
        if (imbalance < 0) return <span className="im-iv im-iv-neg">{imbalance}</span>;
        return <span className="im-iv im-iv-pos">+{imbalance}</span>;
    };

    return (
        <tr>
            <td className="td-rm">
                <IconButton
                    aria-label="Remove record"
                    size="xs"
                    variant="transparent"
                    onClick={onRemove}
                >
                    <Close size="s" />
                </IconButton>
            </td>
            <td><strong>{material}</strong></td>
            <td>{size}</td>
            <td>{quality}</td>
            <td>{coo}</td>
            <td>{wmsQty === 0 ? <span style={{fontWeight: 600}}>0</span> : wmsQty}</td>
            <td>{ciBondedQty > 0 ? ciBondedQty : <span style={{color: 'var(--pds-color-text-secondary)'}}>0</span>}</td>
            <td>{ciNonBondedQty > 0 ? ciNonBondedQty : <span style={{color: 'var(--pds-color-text-secondary)'}}>0</span>}</td>
            <td><strong>{ciTotalQty}</strong></td>
            <td>{renderImbalance()}</td>
            <td style={{fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>
                {persistentImbalance > 0 ? '+' : ''}{persistentImbalance}
            </td>
        </tr>
    );
}
